import { ROOT_KEY } from './copc-octree';

/** Texels per row of the texture, which keeps it within any GPU's size limit. */
export const VISIBLE_NODES_WIDTH = 256;

export interface VisibleNodes {
  /** A texel per node: the mask of the children in the tree, and the index of the first of them. */
  data: Uint8Array;
  count: number;
  /** Each node's texel. */
  indices: Map<string, number>;
}

const parse = (key: string) => key.split('-').map(Number) as [number, number, number, number];

/**
 * The tree of the drawn nodes and their ancestors, breadth first, so the shader can walk down from
 * a node to the deepest drawn node at each point and size the point by that level's spacing. A
 * node's children in the tree come one after another, in the octant order of their keys.
 */
export function buildVisibleNodes(drawnKeys: Iterable<string>): VisibleNodes {
  const inTree = new Set<string>();
  for (const key of drawnKeys) {
    const [depth, x, y, z] = parse(key);
    for (let up = 0; up <= depth; up++) {
      inTree.add(`${depth - up}-${x >> up}-${y >> up}-${z >> up}`);
    }
  }

  const indices = new Map<string, number>();
  const order: string[] = [];
  if (inTree.has(ROOT_KEY)) {
    indices.set(ROOT_KEY, 0);
    order.push(ROOT_KEY);
  }

  const data = new Uint8Array(order.length === 0 ? 0 : inTree.size * 4);
  for (let i = 0; i < order.length; i++) {
    const [depth, x, y, z] = parse(order[i]);
    let mask = 0;
    let firstChild = 0;
    for (let octant = 0; octant < 8; octant++) {
      const child = `${depth + 1}-${x * 2 + (octant & 1)}-${y * 2 + ((octant >> 1) & 1)}-${
        z * 2 + ((octant >> 2) & 1)
      }`;
      if (inTree.has(child)) {
        firstChild = mask === 0 ? order.length : firstChild;
        mask |= 1 << octant;
        indices.set(child, order.length);
        order.push(child);
      }
    }
    data[i * 4] = mask;
    data[i * 4 + 1] = firstChild & 0xff;
    data[i * 4 + 2] = firstChild >> 8;
  }

  return { data, count: order.length, indices };
}
